import React, { useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Stack } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useToast } from '../../lib/useToast';
import { getApiErrorMessage } from '../../lib/toast';
import { uploadPayoutImage, updatePayoutSettings } from '../../services/payoutService';

interface Props {
  kind: 'banner' | 'icon';
  label: string;
  hint: string;
  currentUrl: string;
  canEdit: boolean;
  /** Preview box size — banner is wide, icon is square. */
  previewWidth: number;
  previewHeight: number;
  onSaved: () => void; // reload settings after a save/remove
}

/**
 * Upload → preview (staged) → Save flow for a bonus-push image, reusing the existing S3 image-upload
 * endpoint. Uploading only stages a preview; nothing is persisted until "Save this image".
 */
const FcmImagePicker: React.FC<Props> = ({ kind, label, hint, currentUrl, canEdit, previewWidth, previewHeight, onSaved }) => {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [staged, setStaged] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const patchKey = kind === 'banner' ? 'bannerUrl' : 'iconUrl';
  const preview = staged ?? currentUrl;
  const isDirty = staged !== null && staged !== currentUrl;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploading(true);
    try {
      const res = await uploadPayoutImage(kind, file);
      setStaged(res.data.imageUrl);
      toast.success('Uploaded — preview below. Click “Save this image” to apply.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const persist = async (url: string, successMsg: string) => {
    setSaving(true);
    try {
      await updatePayoutSettings({ [patchKey]: url } as any);
      toast.success(successMsg);
      setStaged(null);
      onSaved();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minWidth: previewWidth + 24 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>{hint}</Typography>

      <Box
        sx={{
          width: previewWidth, height: previewHeight, borderRadius: 1.5, mb: 1,
          border: '1px dashed', borderColor: isDirty ? 'primary.main' : 'divider',
          bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {preview ? (
          <img src={preview} alt={label} style={{ width: '100%', height: '100%', objectFit: kind === 'banner' ? 'cover' : 'contain' }} />
        ) : (
          <Typography variant="caption" color="text.secondary">No image set</Typography>
        )}
      </Box>

      <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="small" variant="outlined" startIcon={uploading ? <CircularProgress size={14} /> : <PhotoCameraIcon fontSize="small" />}
          onClick={() => fileRef.current?.click()} disabled={!canEdit || uploading || saving}
        >
          {currentUrl || staged ? 'Change' : 'Choose image'}
        </Button>
        {isDirty && (
          <Button size="small" variant="contained" onClick={() => persist(staged!, `${label} saved`)} disabled={saving}>
            {saving ? 'Saving…' : 'Save this image'}
          </Button>
        )}
        {isDirty && (
          <Button size="small" color="inherit" onClick={() => setStaged(null)} disabled={saving}>Discard</Button>
        )}
        {!isDirty && currentUrl && (
          <Button
            size="small" color="inherit" startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={() => persist('', `${label} removed`)} disabled={!canEdit || saving}
          >
            Remove
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default FcmImagePicker;
